/**
 * Upload-flow wording (design note QyNFoApoV4Fg2VYcyJ2fFJ, Phase 1, 29 Oct 2026).
 * One place for the words the Plans list, side panel and plan page share, so a
 * rename happens once: "Account plan status" (not "Pipeline status"), "Open
 * published plan" (not "Open full plan"), "View source" (not "Source plan"),
 * "Briefing Questions" (not "Q&A breakdown"), the step names, and the one
 * universal button on every "Action needed" row.
 */
export const FLOW = {
  // Step names (F-UF-F21): every screen that names a step uses these words, via
  // TEAM_STEP / SPONSOR_STEP below. The sponsor's three steps reuse the team's
  // words for the same things: Briefing Questions, View source.
  upload: "Upload",
  reviewBriefing: "Review briefing",
  publish: "Publish",
  briefing: "Briefing",
  status: "Account plan status",
  lastPublished: "Last published",
  lastActivity: "Last Activity",
  openPublished: "Open published plan",
  viewSource: "View source",
  takeAction: "Take action",
  // Row buttons for everyone else (board 01, 2026-10-31).
  viewPlan: "View plan",
  // The AE's row buttons (design note D41).
  addPlan: "Add plan",
  // What the CSM and others read (design note D16; Q9, Sushant 2026-10-31).
  waitingAeUpload: "Waiting on the AE to upload",
  aeReviewingUpload: "Waiting on the AE · reviewing the new upload",
  waitingAePublish: "waiting on the AE to publish",
  // D40: the processing steps' row lines.
  queuedForParsing: "Plan is queued up for parsing",
  parsing: "Parsing the slides",
  briefingQueued: "Briefing waiting in line",
  writingBriefingFor: "Writing the briefing for",
  // D47: what the sponsor sees while a first briefing is written.
  sponsorBriefingWriting: "Your briefing is being written",
  sponsorBriefingWritingSub:
    "Your account team is preparing it. It appears here once it's written.",
  updatePlan: "Update plan",
  viewProgress: "View progress",
  noPlanYet: "No plan yet",
  goToProblem: "Go to the problem",
  briefingQuestions: "Briefing Questions",
  // The three things one briefing run produces (BriefingParts, 2026-10-31).
  briefingPart: "Briefing",
  askAboutPlan: "Ask about this plan",
  versionHistory: "Version history",
  rejectedUploads: "Rejected uploads",
  // Version history's group of stopped uploads (Sushant, 2026-10-31: "Rejected
  // uploads (1) · none became a version, none reached the sponsor" was confusing).
  notUsedUploads: "Earlier uploads not used",
  currentUpload: "Current upload",
  noPublished: "No published plan yet",
  // Confirm plan (Phase 2): every upload stops here before any briefing is written.
  confirmPlan: "Confirm plan",
  confirmThePlan: "Confirm the plan",
  confirmNewUpload: "Confirm the new upload",
  waitingConfirmUpload: "Waiting on the AE to confirm the new upload",
  waitingConfirmPlan: "Waiting on the AE to confirm the plan",
  newUploadWaiting: "New upload waiting to be confirmed",
  writeBriefing: "Write the briefing",
  // Confirm plan with something flagged (product review 2026-11-01): pressing it is
  // the AE's OK, which replaced the "I've checked this" tick.
  useFileWriteBriefing: "Use this file and write the briefing",
  reviewAndConfirm: "Confirm",
  confirmAndWrite: "Confirm and generate briefing",
  replaceFile: "Replace file",
  addConfirmNote: "Add a note for the team",
  useAnyway: "Confirm anyway and generate briefing",
  ackWarnings: "I've checked this and still want to use this file",
  goToConfirm: "Review",
  waitingToConfirm: "Waiting to confirm",
  discarded: "Discarded",
  moved: "Moved",
  youDiscarded: "You discarded it",
  movedAway: "Moved to another account",
  undo: "Undo",
  chatNeedsConfirm: "Available once you confirm the plan",
  chatNeedsBriefing: "Available once the briefing is ready",
  chatNeedsPublish: "Available once the briefing is published",
  // Only when the Briefing Questions couldn't be answered (no Regenerate, 2026-11-01).
  writeBriefingAgain: "Write the briefing again",
  // The AE's optional note when confirming (2026-10-31): kept with the version.
  confirmNoteLabel: "Note for the team (optional)",
  confirmNotePlaceholder:
    "For example: slide 9's org chart is from Q2; the renewal table is current.",
  confirmNoteHelp:
    "Stays with this version: shown in version history, on Review briefing, on the plan and in the side panel.",
  confirmNoteHeading: "AE's note on this plan",
  chatNotConfirmed:
    "Ask about this plan is available once the AE confirms the plan.",
  // Pre-parse rejections (Phase 3): refused before reading, nothing kept.
  rejected: "Rejected",
  rejectedPreParse: "Rejected · pre-parse",
  preParse: "Pre-parse",
  postParse: "Post-parse",
  rejectedBeforeReading:
    "Rejected before reading · No version number used · The file wasn't kept",
  fileNotKept: "File not kept",
  fileWasntKept: "The file wasn't kept.",
  lastUploadRejected: "Last upload rejected",
  uploadDifferent: "Upload a different file",
  // D23: an upload deleted because a newer one became the plan.
  replacedByNewer: "Replaced by a newer version",
  dismiss: "Dismiss",
  // Any upload that didn't become a plan, deleted by the AE (2026-10-31).
  deleteUnused: "Delete",
  deleteUnusedAsk: "Delete it now? This can't be undone.",
} as const;

/** The AE's and CSM's steps by plan-page tab (F-UF-F21): one name each. */
export const TEAM_STEP = {
  plan: FLOW.upload,
  viewer: FLOW.confirmPlan,
  sponsor: FLOW.reviewBriefing,
  publish: FLOW.publish,
} as const;

/** The sponsor's steps by plan-page tab (F-UF-F21), in the team's words. */
export const SPONSOR_STEP = {
  sponsor: FLOW.briefing,
  qa: FLOW.briefingQuestions,
  viewer: FLOW.viewSource,
} as const;

/** "Move it here from Globex" (duplicate_other, when the uploader may move it). */
export const moveHereFrom = (account: string | null) =>
  account ? `Move it here from ${account}` : "Move it here";

/** Why a stopped upload's file is gone: the organization's "keep unused uploads"
 * days at the time (failure audit Q3, one rule for every waiting upload, D23). */
export const deletedAfter = (f: {
  status?: string;
  purged_after_days?: number | null;
}) => `Deleted after ${f.purged_after_days ?? 7} days`;

/** Failure audit Q3/Q5 (D23): when an upload waiting on the AE goes, and when a refusal
 * notice clears (nothing was stored, so nothing is "deleted"). */
export const deletesOn = (date: string) =>
  `Deleted automatically on ${date} if nothing happens`;
export const noticeClearsOn = (date: string) => `This notice clears on ${date}`;

/** An upload "Move it here" took to another account (F-UF-E21); named only for
 * someone who can open that account. */
export const movedTo = (name?: string | null) =>
  name ? `Moved to ${name}` : FLOW.movedAway;

/** duplicate_other the uploader can't move: who can. */
export const askToMove = (uploader: string | null | undefined) =>
  uploader
    ? `Ask ${uploader} or your admin to move it.`
    : "Ask your admin to move it.";

/** The uploader's own copy, published on another account (F-UF-E11): a published
 * plan is never moved silently, and they aren't asked to ask themselves. */
export const ownPublishedElsewhere = (account: string) =>
  `It's published on ${account}. Ask your admin to take it off there first.`;

/**
 * One name per situation (F-UF-F20): the plan page's status strip, the Plans
 * list's problem line, the panel and the history all call a situation the same.
 * The list's bucket ("Action needed" / "In progress") is a separate axis: whose
 * turn it is, not what happened.
 */
export const SITUATION = {
  // Post-parse, can't continue: the AE uploads a fixed file.
  read_failed: "Needs fixing",
  // A blocking plan check failed after reading.
  needs_fixing: "Needs review",
  // Read with warnings: the AE says it's fine (on Confirm plan) or fixes it.
  needs_ok: "Waiting for review",
  // Read and clean: waits on the AE's Confirm plan.
  to_confirm: "Confirm plan",
  // Confirmed, but the briefing couldn't be written (F-UF-F11).
  briefing_failed: "Briefing failed",
} as const;

/** What the newest upload's problem is called (Phase 1 maps today's statuses). */
export const PROBLEM_KIND: Record<
  "read_failed" | "needs_fixing" | "needs_ok",
  string
> = {
  read_failed: SITUATION.read_failed,
  needs_fixing: SITUATION.needs_fixing,
  needs_ok: SITUATION.needs_ok,
};

/**
 * Plan numbers (D1, Phase 3b). A version carries two numbers: `version`, the
 * internal upload sequence (identity, ordering, "newer than"; never shown), and
 * `plan_version`, the number people see, given only when the AE confirms. An
 * unconfirmed upload is never numbered, so every on-screen "vN" goes through
 * these helpers.
 */
interface Numbered {
  plan_version?: number | null;
  created_at?: string | null;
}

// F-UF-F14: a value that isn't a date reads as no date, never "Invalid Date".
const dayMonth = (iso?: string | null): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

/** "v3" once confirmed, else `unnumbered` ("New upload"). */
export const planLabel = (
  f: Numbered | null | undefined,
  unnumbered = "New upload"
): string => (f?.plan_version != null ? `v${f.plan_version}` : unnumbered);

/** In a sentence: "v3", or "the upload from Oct 27" before it is confirmed. */
export const planRef = (f: Numbered | null | undefined): string => {
  if (f?.plan_version != null) return `v${f.plan_version}`;
  const day = dayMonth(f?.created_at);
  return day ? `the upload from ${day}` : "the new upload";
};

/**
 * Same as planRef, for the start of a sentence or a title. A plan number keeps
 * its lowercase "v" ("v3 is published.", F-UF-F13); only the unnumbered form is
 * capitalised ("The upload from Oct 27").
 */
export const PlanRef = (f: Numbered | null | undefined): string => {
  const r = planRef(f);
  if (f?.plan_version != null) return r;
  return r.charAt(0).toUpperCase() + r.slice(1);
};

/**
 * A publish record's number: its plan number, falling back to the internal
 * number only for records written before the D1 backfill (where both match).
 */
export const pubNumber = (
  p: { version: number; plan_version?: number | null } | null | undefined
): number | null => (p ? (p.plan_version ?? p.version) : null);

/** "v3" for a publish record ("" when there is none). */
export const pubLabel = (
  p: { version: number; plan_version?: number | null } | null | undefined
): string => {
  const n = pubNumber(p);
  return n != null ? `v${n}` : "";
};

/**
 * Upload while a draft is pending (rule, Sushant 2026-10-30): a confirmed version
 * whose draft isn't published blocks a new upload until it is published or the
 * AE chooses to replace it.
 */
interface PendingLike {
  plan_version: number | null;
  verified: number;
  owned: number;
  csm_names?: string[] | null;
}

const draftOf = (p: PendingLike) =>
  p.plan_version != null ? `v${p.plan_version}'s draft` : "The current draft";

/** "Ana", "Ana and Ben", "Ana, Ben and Cy". */
export const joinNames = (names: string[]): string =>
  names.length <= 1
    ? names[0] || ""
    : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

/** "v3's draft is in review (4 of 8 verified). Publish it, or replace it, before uploading a new plan." */
export const pendingDraftNote = (p: PendingLike): string =>
  `${draftOf(p)} is in review (${p.verified} of ${p.owned} verified). Publish it, or replace it, before uploading a new plan.`;

/** The inline confirm before "Replace this draft" opens the file picker. */
export const replaceDraftWarning = (p: PendingLike): string => {
  const names = (p.csm_names || []).filter(Boolean);
  const notified = names.length ? ` ${joinNames(names)} will be notified.` : "";
  return `${draftOf(p)} will be set aside. Verifications on sections that change will need redoing.${notified}`;
};

export const REPLACE_DRAFT = "Replace this draft";

/** The sponsor during an update (board 02, 2026-10-31): they keep the published
 * briefing, with a link to the newer draft and back. */
export const SPONSOR_DRAFT = {
  newer: (v: string) =>
    `A newer draft${v ? ` (${v})` : ""} is being reviewed by your account team. You're reading the published briefing.`,
  viewDraft: "View draft",
  reading: (v: string) =>
    `You're reading the${v ? ` ${v}` : ""} draft. Your account team is still reviewing it.`,
  backToPublished: "Back to the published briefing",
};
